import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, RefreshCw, Trash2 } from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";
import { GLANCE_EVENT, deleteGlance, listGlance, scoreGlance } from "../lib/glanceApi";
import { IQ_BUCKETS, filterByRange, glanceStats, money, pct, roi, wpRoi } from "../lib/glanceStats";
import { todayLocalIso } from "../lib/dateHelpers";

const monoStyle = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const btn =
  "inline-flex items-center gap-2 px-3 h-8 rounded border border-stone-600 hover:border-[#84cc16] hover:text-[#84cc16] text-stone-200 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors disabled:opacity-50";
const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 Days" },
  { id: "all", label: "All Time" },
];

const RateRow = ({ label, s, testId }) => (
  <tr className="border-t border-stone-800" data-testid={testId}>
    <td className="py-2 pr-4 text-[#f5ecd6]">{label}</td>
    <td className="py-2 px-3 text-center text-stone-400">{s.n}</td>
    <td className="py-2 px-3 text-center text-[#84cc16] font-bold">{pct(s.win, s.n)}</td>
    <td className="py-2 px-3 text-center text-[#a3e635]">{pct(s.top2, s.n)}</td>
    <td className="py-2 px-3 text-center text-[#d9f99d]">{pct(s.top3, s.n)}</td>
    <td className="py-2 px-3 text-center text-stone-300">{money(s.bet)}</td>
    <td className="py-2 px-3 text-center text-stone-300">{money(s.ret)}</td>
    <td
      className={`py-2 px-3 text-center font-bold ${s.bet && s.ret >= s.bet ? "text-[#84cc16]" : "text-[#f87171]"}`}
      data-testid={`${testId}-roi`}
    >
      {roi(s)}
    </td>
    <td className="py-2 px-3 text-center text-stone-300 border-l border-stone-800">{money(s.wpBet)}</td>
    <td className="py-2 px-3 text-center text-stone-300">{money(s.wpRet)}</td>
    <td
      className={`py-2 px-3 text-center font-bold ${s.wpBet && s.wpRet >= s.wpBet ? "text-[#84cc16]" : "text-[#f87171]"}`}
      data-testid={`${testId}-wp-roi`}
    >
      {wpRoi(s)}
    </td>
  </tr>
);

const Posts = ({ posts }) => (
  <span className="inline-flex gap-1 align-middle">
    {posts.map((p) => (
      <SaddleCloth key={p} n={p} />
    ))}
  </span>
);

const SnapRow = ({ s, onDelete }) => (
  <tr className="border-t border-stone-800 text-[11px]" data-testid={`glance-snap-${s.id}`}>
    <td className="py-2 pr-3 text-stone-300 whitespace-nowrap">{s.date} · {s.track} R{s.race}</td>
    <td className="py-2 px-2"><Posts posts={s.strong} /></td>
    <td className="py-2 px-2"><Posts posts={s.key != null ? [s.key] : []} /></td>
    <td className="py-2 px-2"><Posts posts={s.longshots} /></td>
    <td className="py-2 px-2 text-stone-300 whitespace-nowrap">
      {s.wps ? s.wps.join("-") : "pending"}
      {s.win_payoff ? <span className="text-stone-500"> · ${Number(s.win_payoff).toFixed(2)}</span> : null}
    </td>
    <td className="py-2 pl-2 text-right">
      <button type="button" onClick={() => onDelete(s.id)} data-testid={`glance-snap-delete-${s.id}`} className="text-stone-500 hover:text-[#f87171]">
        <Trash2 size={14} />
      </button>
    </td>
  </tr>
);

const RangeButtons = ({ range, setRange }) => (
  <div className="flex gap-2 flex-wrap">
    {RANGES.map((r) => (
      <button
        key={r.id}
        type="button"
        onClick={() => setRange(r.id)}
        data-testid={`glance-range-${r.id}`}
        className={`px-3 h-8 rounded text-[11px] uppercase tracking-[0.18em] border transition-colors ${
          range === r.id
            ? "bg-[#84cc16] text-[#0a0a0a] border-[#84cc16] font-bold"
            : "border-stone-700 text-stone-300 hover:border-[#84cc16]"
        }`}
      >
        {r.label}
      </button>
    ))}
  </div>
);

export const GlancePerformance = () => {
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState("all");
  const load = useCallback(() => listGlance().then(setSnaps).catch(() => {}), []);

  useEffect(() => {
    load();
    window.addEventListener(GLANCE_EVENT, load);
    return () => window.removeEventListener(GLANCE_EVENT, load);
  }, [load]);

  const onScore = async () => {
    setBusy(true);
    try {
      const r = await scoreGlance();
      toast.success(r.scored ? `Scored ${r.scored} race${r.scored === 1 ? "" : "s"}` : "No new official results yet");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const onDelete = (id) => deleteGlance(id).catch((e) => toast.error(e.message));

  const shown = filterByRange(snaps, range, todayLocalIso());
  const st = glanceStats(shown);
  return (
    <section className="mt-8 mx-auto max-w-5xl rounded-lg border border-stone-800 bg-[#0e0e0e] p-5 space-y-4" style={monoStyle} data-testid="glance-performance">
      <div className="flex items-center gap-3 flex-wrap">
        <BarChart3 size={16} className="text-[#84cc16]" />
        <span className="text-sm uppercase tracking-[0.2em] text-[#f5ecd6]">Glance Performance</span>
        <span className="text-[10px] text-stone-500" data-testid="glance-perf-count">
          {st.scored} scored / {shown.length} saved
        </span>
        <button type="button" onClick={onScore} disabled={busy} className={`ml-auto ${btn}`} data-testid="glance-score-btn">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Score Results
        </button>
      </div>
      <RangeButtons range={range} setRange={setRange} />
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
              <th className="text-left font-normal pb-1">Category</th>
              <th className="font-normal pb-1">Races</th>
              <th className="font-normal pb-1">Win</th>
              <th className="font-normal pb-1">Top 2</th>
              <th className="font-normal pb-1">Top 3</th>
              <th className="font-normal pb-1">$2 Win Bets</th>
              <th className="font-normal pb-1">Return</th>
              <th className="font-normal pb-1">ROI</th>
              <th className="font-normal pb-1 border-l border-stone-800">$2 W/P Bets</th>
              <th className="font-normal pb-1">W/P Return</th>
              <th className="font-normal pb-1">W/P ROI</th>
            </tr>
          </thead>
          <tbody>
            <RateRow label="Strong Contenders" s={st.strong} testId="glance-rate-strong" />
            <RateRow label="The Key Horse" s={st.key} testId="glance-rate-key" />
            <RateRow label="Long Shot Play" s={st.longshot} testId="glance-rate-longshot" />
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-stone-500">
        ROI = a $2 win bet on every horse in the category, paid at the official $2 win price. W/P = $2 to win
        plus $2 to place on every horse ($4 each), paid the official win and place prices.
      </div>
      <div data-testid="glance-iq-dist">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-2">Winner's IQ Power rank</div>
        <div className="flex flex-wrap gap-2">
          {IQ_BUCKETS.map((b) => (
            <div key={b} className="px-3 py-1.5 rounded border border-stone-800 text-center" data-testid={`glance-iq-bucket-${b}`}>
              <div className="text-[9px] text-stone-500">{b === "Unranked" ? b : `IQ #${b}`}</div>
              <div className="text-sm text-[#84cc16] font-bold">{pct(st.iq[b], st.scored)}</div>
            </div>
          ))}
        </div>
      </div>
      {shown.length > 0 && (
        <div className="max-h-80 overflow-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-stone-500">
                <th className="text-left font-normal">Race</th>
                <th className="text-left font-normal px-2">Strong</th>
                <th className="text-left font-normal px-2">Key</th>
                <th className="text-left font-normal px-2">Long Shot</th>
                <th className="text-left font-normal px-2">Result</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <SnapRow key={s.id} s={s} onDelete={onDelete} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
